import React from "react";

import {
  assignAccessRole,
  fetchAccessRoles,
  fetchAccessMembers,
  revokeAccessRole,
  toggleRbacEnabled,
  inviteMember,
  updateMemberProfile,
  updateMemberStatus,
  type AccessRole,
  type AccessMember,
  type AccessInvite,
} from "../api/accessAdminClient";

const emptyState = {
  title: "Access restricted",
  description: "You do not have permission to manage access. Contact your workspace administrator.",
};

export function AccessSettingsScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [members, setMembers] = React.useState<AccessMember[]>([]);
  const [invites, setInvites] = React.useState<AccessInvite[]>([]);
  const [roles, setRoles] = React.useState<AccessRole[]>([]);
  const [rbacEnabled, setRbacEnabled] = React.useState(false);
  const [forbidden, setForbidden] = React.useState(false);
  const [pendingRoleUpdate, setPendingRoleUpdate] = React.useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [inviteRoleId, setInviteRoleId] = React.useState<string>("");
  const [pendingInvite, setPendingInvite] = React.useState(false);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const [membersPayload, rolesPayload] = await Promise.all([fetchAccessMembers(), fetchAccessRoles()]);
      setMembers(membersPayload.members ?? []);
      setInvites(membersPayload.invites ?? []);
      setRoles(rolesPayload.roles ?? []);
      setRbacEnabled(Boolean(membersPayload.rbacEnabled));
    } catch (err) {
      if (err instanceof Error && (err as any)?.status === 403) {
        setForbidden(true);
      } else {
        setError(err instanceof Error ? err.message : "Failed to load access settings");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleToggle = async () => {
    const confirmed = window.confirm(
      `This will ${rbacEnabled ? "disable" : "enable"} RBAC enforcement for the workspace. Continue?`,
    );
    if (!confirmed) return;
    try {
      const reason = window.prompt("Reason for RBAC change") ?? "";
      if (!reason.trim()) {
        return;
      }
      const result = await toggleRbacEnabled(!rbacEnabled, reason);
      setRbacEnabled(result.rbacEnabled);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update RBAC setting");
    }
  };

  const handleAssignRole = async (userId: string, roleId: string | null) => {
    if (!roleId) return;
    setPendingRoleUpdate(userId);
    try {
      const reason = window.prompt("Reason for role change") ?? "";
      if (!reason.trim()) {
        setPendingRoleUpdate(null);
        return;
      }
      const userRoles = members.find((entry) => entry.user.id === userId)?.roles ?? [];
      const existing = userRoles.find((role) => role.id === roleId);
      if (existing) {
        await revokeAccessRole(userId, roleId, reason);
      } else {
        await assignAccessRole(userId, roleId, reason);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setPendingRoleUpdate(null);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setPendingInvite(true);
    try {
      const reason = window.prompt("Reason for inviting this member") ?? "";
      if (!reason.trim()) {
        setPendingInvite(false);
        return;
      }
      await inviteMember(inviteEmail.trim(), inviteRoleId ? [{ roleId: inviteRoleId }] : [], reason);
      setInviteEmail("");
      setInviteRoleId("");
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite member");
    } finally {
      setPendingInvite(false);
    }
  };

  const handleProfileUpdate = async (memberId: string, groups: string[]) => {
    try {
      await updateMemberProfile(memberId, { functionGroups: groups });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
    }
  };

  const handleStatusUpdate = async (memberId: string, status: string) => {
    const reason = window.prompt("Reason for membership status change") ?? "";
    if (!reason.trim()) return;
    try {
      await updateMemberStatus(memberId, status, reason);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update member status");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Loading access settings…</div>;
  }

  if (forbidden) {
    return (
      <div className="p-6">
        <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">{emptyState.title}</h2>
          <p className="mt-2 text-sm text-slate-600">{emptyState.description}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Access control</h2>
            <p className="text-sm text-slate-600">Enable RBAC enforcement for this workspace.</p>
          </div>
          <button
            type="button"
            className={`button ${rbacEnabled ? "button-primary" : "button-secondary"}`}
            onClick={handleToggle}
          >
            {rbacEnabled ? "RBAC enabled" : "RBAC disabled"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Users</h3>
        <p className="text-sm text-slate-600">Assign roles and functional groups for the current tenant.</p>
        <div className="mt-4 overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Roles</th>
                <th className="py-2 pr-4">Function groups</th>
                <th className="py-2 pr-4">Assign</th>
              </tr>
            </thead>
            <tbody className="text-slate-900">
              {members.map((member) => (
                <tr key={member.id} className="border-t border-slate-100">
                  <td className="py-3 pr-4">{member.user.email}</td>
                  <td className="py-3 pr-4">
                    <select
                      className="input"
                      value={member.status}
                      onChange={(event) => handleStatusUpdate(member.id, event.target.value)}
                    >
                      {["INVITED", "ACTIVE", "SUSPENDED", "REMOVED"].map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 pr-4">
                    {member.roles.length ? member.roles.map((role) => role.name).join(", ") : "—"}
                  </td>
                  <td className="py-3 pr-4">
                    <select
                      className="input"
                      multiple
                      value={member.profile?.functionGroups ?? []}
                      onChange={(event) => {
                        const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
                        void handleProfileUpdate(member.id, selected);
                      }}
                    >
                      {["FINANCE", "IT", "COMPLIANCE", "HR", "BUSINESS_MGMT", "TOP_MGMT"].map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 pr-4">
                    <select
                      className="input"
                      onChange={(event) => handleAssignRole(member.user.id, event.target.value)}
                      value=""
                      disabled={pendingRoleUpdate === member.user.id}
                    >
                      <option value="">Select role</option>
                      {roles.map((role) => {
                        const isAssigned = member.roles.some((assigned) => assigned.id === role.id);
                        return (
                          <option key={role.id} value={role.id}>
                            {isAssigned ? "Remove" : "Assign"} {role.name}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Invitations</h3>
        <p className="text-sm text-slate-600">Invite members to join the tenant and assign a role.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            className="input"
            placeholder="Email address"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
          />
          <select className="input" value={inviteRoleId} onChange={(event) => setInviteRoleId(event.target.value)}>
            <option value="">Select role</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          <button type="button" className="button button-primary" onClick={handleInvite} disabled={pendingInvite}>
            Send invite
          </button>
        </div>
        <div className="mt-4 space-y-2 text-sm text-slate-700">
          {invites.length === 0 ? (
            <div className="text-slate-500">No pending invites.</div>
          ) : (
            invites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between rounded border border-slate-100 p-3">
                <span>{invite.email}</span>
                <span className="text-xs uppercase text-slate-500">{invite.status}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
